/**
 * Shared view of the provider's LIVE en-route journey, owned by the app-wide
 * live-ping controller (`features/sessions/components/live-journey-ping-controller.tsx`).
 *
 * WHY: live pings used to run inside `JourneyFlowCard`, which only exists while
 * the session-detail screen is open. Leaving that screen (or Maps coming to the
 * front and the card re-rendering away) silently stopped the pings and disarmed
 * the native pinger (2026-10-19 field test). The controller now owns pinging
 * for as long as the journey is en route; this tiny store is how the
 * screen-level UI (the journey card, session detail) still sees what the
 * controller learned:
 *
 *  - `journey`  — the latest journey the controller (or session detail)
 *    published, tagged with its `origin` so each side only adopts the OTHER's
 *    updates (never an echo of its own);
 *  - `presence` — live position / GPS state / warning for the card's ETA tile.
 *
 * Ephemeral (not persisted); one session at a time. A `useSyncExternalStore`
 * store, per the repo's state rules.
 */

import { useSyncExternalStore } from 'react'
import type { GeoPoint, SessionJourney } from '@shared/sessions/types'

export type LiveJourneyOrigin = 'controller' | 'detail'

export interface LiveJourneyPublication {
  sessionUuid: string
  journey: SessionJourney | null
  origin: LiveJourneyOrigin
}

export interface LiveJourneyPresence {
  sessionUuid: string
  currentPosition: GeoPoint | null
  gpsActive: boolean
  gpsUnavailable: boolean
  /** e.g. "Couldn't get precise location — using estimate" (cleared on the next good ping). */
  warning: string | null
}

let publication: LiveJourneyPublication | null = null
let presence: LiveJourneyPresence | null = null
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

export function subscribeLiveJourney(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getLiveJourneyPublication(): LiveJourneyPublication | null {
  return publication
}

export function getLiveJourneyPresence(): LiveJourneyPresence | null {
  return presence
}

/**
 * Publish the journey for a session. A no-op when it is the very object already
 * published for that session (so adopt → re-publish can never loop).
 */
export function publishLiveJourney(next: LiveJourneyPublication): void {
  if (publication && publication.sessionUuid === next.sessionUuid && publication.journey === next.journey) return
  publication = next
  emit()
}

export function publishLiveJourneyPresence(next: LiveJourneyPresence | null): void {
  if (presence === next) return
  if (
    presence &&
    next &&
    presence.sessionUuid === next.sessionUuid &&
    presence.currentPosition === next.currentPosition &&
    presence.gpsActive === next.gpsActive &&
    presence.gpsUnavailable === next.gpsUnavailable &&
    presence.warning === next.warning
  ) {
    return
  }
  presence = next
  emit()
}

/** Forget everything (journey ended / session changed / sign-out). */
export function resetLiveJourney(): void {
  if (!publication && !presence) return
  publication = null
  presence = null
  emit()
}

/** The latest publication for `sessionUuid` (null for any other session). */
export function useLiveJourneyPublication(sessionUuid: string | null): LiveJourneyPublication | null {
  const current = useSyncExternalStore(subscribeLiveJourney, getLiveJourneyPublication, getLiveJourneyPublication)
  return current && sessionUuid != null && current.sessionUuid === sessionUuid ? current : null
}

/** Live presence for `sessionUuid` (null when the controller is not tracking it). */
export function useLiveJourneyPresence(sessionUuid: string | null): LiveJourneyPresence | null {
  const current = useSyncExternalStore(subscribeLiveJourney, getLiveJourneyPresence, getLiveJourneyPresence)
  return current && sessionUuid != null && current.sessionUuid === sessionUuid ? current : null
}

/** TEST ONLY. */
export function __resetLiveJourneyStoreForTests(): void {
  publication = null
  presence = null
  listeners.clear()
}
